import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, MessageCircle, TrendingUp } from "lucide-react";
import { API } from "../../lib/api";
import { waLink } from "../../data/config";
import { gfUrl } from "../common/GrowthFactorCTA";
import { track } from "../../lib/track";

const TREATMENT_NAMES = {
  "conventional-braces": "Conventional Braces",
  "clear-aligners": "Clear Aligners",
  frc: "FRC",
  "zirconia-crowns-bridges": "Zirconia Crowns & Bridges",
  "aesthetic-veneers": "Veneers / Smile Makeover",
};

const contextualWaMessage = (pathname) => {
  const tMatch = pathname.match(/\/treatments\/([a-z-]+)/);
  if (tMatch && TREATMENT_NAMES[tMatch[1]]) {
    const t = TREATMENT_NAMES[tMatch[1]];
    return `Hi, saya datang melalui iINSPIRE Dental dan ingin bertanya tentang ${t}.\nSource: Dental Website — ${t}`;
  }
  if (pathname.startsWith("/dental"))
    return "Hi, saya datang melalui iINSPIRE Dental dan ingin bertanya.\nSource: Dental Website — Floating WhatsApp";
  if (pathname.includes("growth-factor"))
    return "Hi, saya datang melalui iINSPIRE Consulting dan ingin bertanya tentang Growth Factor.\nSource: Consulting Website — Growth Factor";
  if (pathname.startsWith("/consulting"))
    return "Hi, saya datang melalui iINSPIRE Consulting dan ingin bertanya.\nSource: Consulting Website — Floating WhatsApp";
  if (pathname.startsWith("/creative"))
    return "Hi, saya datang melalui iINSPIRE Creative dan ingin bertanya.\nSource: Creative Website — Floating WhatsApp";
  return "Hi, saya datang melalui iINSPIRE dan ingin bertanya.\nSource: iINSPIRE Website — Floating WhatsApp";
};

const OPENERS = {
  ms: "Hi, saya Aira 🌿 Pembantu digital iINSPIRE. Apa yang Aira boleh bantu?",
  en: "Hi, I'm Aira 🌿 the iINSPIRE digital concierge. How can I help?",
  zh: "您好，我是 Aira 🌿 iINSPIRE 数字礼宾助理。有什么可以帮您？",
  ta: "வணக்கம், நான் Aira 🌿 iINSPIRE டிஜிட்டல் உதவியாளர். எப்படி உதவலாம்?",
  id: "Hai, saya Aira 🌿 asisten digital iINSPIRE. Ada yang bisa Aira bantu?",
};

const LANGUAGES = [
  { id: "ms", label: "BM" },
  { id: "en", label: "EN" },
  { id: "zh", label: "中文" },
  { id: "ta", label: "தமிழ்" },
  { id: "id", label: "ID" },
];

const CHIPS = {
  dental: ["Clear Aligners", "Conventional Braces", "Veneers / Smile Makeover", "I'm not sure yet"],
  consulting: ["Growth Factor RM15", "Brand2Market", "Speaking / Training", "Business consulting"],
  publishing: ["Books & Writing", "Poetry & Essays", "Collaboration"],
  general: ["Dental & Senyuman", "Bisnes & Growth", "Training / Consulting", "Coffee With Dr. Irma", "Buku & Penulisan"],
};

const contextFromPath = (pathname) => {
  if (pathname.startsWith("/dental")) return "dental";
  if (pathname.startsWith("/consulting")) return "consulting";
  if (pathname.startsWith("/creative")) return "publishing";
  return "general";
};

const slugFromPath = (p) => {
  const m = p.match(/\/(?:consulting\/insights|creative\/stories|dental\/[a-z]{2}\/kolum)\/([a-z0-9-]+)/);
  return m ? m[1] : "";
};

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const AiraWidget = () => {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(() => {
    const m = window.location.pathname.match(/^\/dental\/([a-z]{2})(\/|$)/);
    return m && ["ms", "en", "zh", "ta"].includes(m[1]) ? m[1] : "en";
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [showGfCta, setShowGfCta] = useState(false);
  const [appointmentLead, setAppointmentLead] = useState(null);
  const location = useLocation();
  const vertical = contextFromPath(location.pathname);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);
  const firedSignals = useRef(new Set());

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("aira:open", handler);
    return () => window.removeEventListener("aira:open", handler);
  }, []);

  if (!sessionRef.current) {
    sessionRef.current = sessionStorage.getItem("aira_session") || `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("aira_session", sessionRef.current);
  }

  useEffect(() => {
    if (open) {
      if (messages.length === 0) {
        setMessages([{ role: "assistant", content: OPENERS[language] }]);
      }
      if (!firedSignals.current.has("aira_open")) {
        firedSignals.current.add("aira_open");
        track("aira_open", { vertical, page: location.pathname });
      }
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    if (messages.length <= 1) setMessages([{ role: "assistant", content: OPENERS[lang] }]);
  };

  const fireSignal = (key, event, params = {}) => {
    if (firedSignals.current.has(key)) return;
    firedSignals.current.add(key);
    track(event, { source: "aira", vertical, page: location.pathname, ...params });
  };

  const handleSignals = (s) => {
    const tSlug = slugify(s.treatment);
    if (tSlug) fireSignal(`t:${tSlug}`, "aira_treatment_interest", { treatment_category: tSlug });
    if (s.appointment_started) fireSignal("appt_started", "aira_appointment_started");
    if (s.high_intent) fireSignal("high_intent", "aira_high_intent");
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    fireSignal("conv_started", "aira_conversation_started");
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/aira/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg, vertical, page: location.pathname, language }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ") && !line.includes("[DONE]")) {
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.delta) {
                fullText += payload.delta;
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
                  return copy;
                });
              }
              if (payload.error) {
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { role: "assistant", content: payload.error };
                  return copy;
                });
              }
              if (payload.signals) {
                handleSignals(payload.signals);
              }
              if (payload.appointment_ready) {
                setAppointmentLead(payload.lead || {});
                track("appointment_qualified", { source: "aira", vertical, page: location.pathname });
                fireSignal("appt_done", "aira_appointment_request_completed", { treatment_category: slugify(payload.lead?.treatment) });
              }
            } catch {}
          }
        }
      }
      if (fullText.includes("[APPOINTMENT_DATA]")) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content.replace(/\s*\[APPOINTMENT_DATA\][\s\S]*$/, "").trim() };
          return copy;
        });
      }
      if (fullText.includes("[GF_RM15]")) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content.replace("[GF_RM15]", "").trim() };
          return copy;
        });
        setShowGfCta(true);
        track("gf_cta_impression", { source: "aira", article: slugFromPath(location.pathname), campaign: "growth-factor-rm15", cta_position: "aira-chat" });
      }
      setShowHandoff(true);
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Aira is momentarily unavailable — please reach us on WhatsApp below." };
        return copy;
      });
      setShowHandoff(true);
    } finally {
      setStreaming(false);
    }
  };

  const handleChip = (c) => {
    if (vertical === "dental" && c !== "I'm not sure yet") {
      fireSignal(`t:${slugify(c)}`, "aira_treatment_interest", { treatment_category: slugify(c) });
    }
    send(c);
  };

  const handoff = async () => {
    track("aira_whatsapp_handoff", { vertical, page: location.pathname, handoff_type: appointmentLead ? "appointment" : "general" });
    track("whatsapp_click", { vertical, page: location.pathname, link_id: appointmentLead ? "aira-appointment-whatsapp" : "aira-handoff" });
    try {
      const res = await fetch(`${API}/aira/handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, vertical, language, page: location.pathname, requested_contact: true }),
      });
      const data = await res.json();
      window.open(data.whatsapp_url, "_blank");
    } catch {}
  };

  return (
    <>
      {!open && (
      <div className="fixed bottom-5 right-5 z-50 flex items-end gap-3 sm:bottom-6 sm:right-6" data-testid="floating-contact-pair">
        <motion.a
          href={waLink(contextualWaMessage(location.pathname))}
          target="_blank"
          rel="noreferrer"
          aria-label="Contact iINSPIRE on WhatsApp"
          data-testid="floating-whatsapp"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition-transform duration-300 hover:scale-105 sm:h-13 sm:w-13"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.7, type: "spring", stiffness: 200 }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 0 2.893 6.994c-.003 5.45-4.437 9.884-9.895 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
          </svg>
        </motion.a>
        <div className="group relative">
          <span className="pointer-events-none absolute right-full top-1/2 mr-3 hidden -translate-y-1/2 whitespace-nowrap rounded-full bg-ink px-3 py-1.5 text-[11px] font-medium text-warmwhite opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:block">
            Talk to Aira
          </span>
          <motion.button
            onClick={() => setOpen(!open)}
            data-testid="aira-launcher"
            aria-label="Talk to Aira, iINSPIRE Digital Concierge"
            className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border-2 border-[#FF3B8D] bg-warmwhite shadow-xl transition-transform duration-300 hover:scale-105"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
          >
            <img src="/img/ecosystem/aira-avatar.webp" alt="" className="h-full w-full object-cover" />
          </motion.button>
        </div>
      </div>
      )}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-x-3 bottom-3 z-50 flex h-[68dvh] max-h-[calc(100dvh-7rem)] flex-col border border-ink/10 bg-warmwhite shadow-2xl sm:inset-x-auto sm:bottom-24 sm:right-6 sm:h-[30rem] sm:max-h-[calc(100vh-8rem)] sm:w-[calc(100vw-2rem)] sm:max-w-sm"
            data-testid="aira-panel"
          >
            <div className="flex items-center justify-between border-b border-ink/10 bg-ink px-5 py-4">
              <div>
                <p className="font-serif text-base text-warmwhite">Aira</p>
                <p className="text-[9px] uppercase tracking-[0.2em] text-warmwhite/50">iINSPIRE Digital Concierge · AI assistant</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close Aira" data-testid="aira-close" className="text-warmwhite/60 hover:text-warmwhite">
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex gap-1.5 border-b border-ink/10 px-4 py-2.5" role="group" aria-label="Language">
              {LANGUAGES.map((l) => (
                <button
                  key={l.id}
                  onClick={() => changeLanguage(l.id)}
                  data-testid={`aira-lang-${l.id}`}
                  className={`px-2.5 py-1 text-[10px] font-semibold tracking-[0.1em] ${language === l.id ? "bg-ink text-warmwhite" : "text-ink/50 hover:text-ink"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="aira-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-ink text-warmwhite" : "border border-ink/10 bg-white text-ink"}`}>
                    {m.content.replace("[GF_RM15]", "").replace(/\s*\[APPOINTMENT_DATA\][\s\S]*$/, "")}
                    {m.role === "assistant" && streaming && i === messages.length - 1 && <span className="ml-1 inline-block h-3 w-1.5 animate-pulse bg-amber" />}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {CHIPS[vertical].map((c) => (
                  <button key={c} onClick={() => handleChip(c)} data-testid={`aira-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-ink/15 px-3 py-1.5 text-xs text-ink/70 transition-colors hover:border-amber hover:text-burgundy">
                    {c}
                  </button>
                ))}
              </div>
            )}
            {appointmentLead && (
              <button onClick={handoff} data-testid="aira-appointment-whatsapp" className="mx-4 mb-2 flex items-center justify-center gap-2 border border-[#25D366]/60 bg-[#25D366]/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-[#25D366] hover:text-white">
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Continue to WhatsApp — Appointment Request
              </button>
            )}
            {showGfCta && (
              <a
                href={gfUrl({ source: "aira", article: slugFromPath(location.pathname), position: "aira-chat" })}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  track("gf_cta_click", { source: "aira", article: slugFromPath(location.pathname), campaign: "growth-factor-rm15", cta_position: "aira-chat" });
                  track("growthfactor_checkout_started", { source: "aira", article: slugFromPath(location.pathname), campaign: "growth-factor-rm15", cta_position: "aira-chat" });
                }}
                data-testid="aira-gf-cta"
                className="mx-4 mb-2 flex items-center justify-center gap-2 border border-burgundy/60 bg-burgundy/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-burgundy transition-colors hover:bg-burgundy hover:text-warmwhite"
              >
                <TrendingUp className="h-4 w-4" strokeWidth={1.5} /> Diagnos Bisnes Saya — RM15
              </a>
            )}
            {showHandoff && !appointmentLead && (
              <button onClick={handoff} data-testid="aira-handoff" className="mx-4 mb-2 flex items-center justify-center gap-2 border border-amber/60 bg-amber/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-amber hover:text-ink">
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Connect to the team on WhatsApp
              </button>
            )}
            <div className="flex gap-2 border-t border-ink/10 p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={language === "ms" ? "Tulis mesej…" : "Type a message…"}
                data-testid="aira-input"
                className="flex-1 border border-ink/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber"
              />
              <button onClick={() => send()} disabled={streaming} aria-label="Send" data-testid="aira-send" className="bg-ink px-4 text-warmwhite transition-colors hover:bg-burgundy disabled:opacity-50">
                <Send className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
